// score.js - Composite end-of-run score.
//
// Design goal: reward SKILL over RNG.
//   - Height is the backbone and the clearest skill signal (platforming).
//   - Coins are scored by PERCENTAGE collected, not raw count, so a player who
//     grabs everything on a short run isn't beaten purely because RNG spawned
//     more coins on someone else's run.
//   - Kills are weighted by shooting ACCURACY: 3 kills with 4 shots is worth far
//     more than 3 kills with 20 shots. Raw kill count is partly RNG (how many
//     enemies spawn), so accuracy is what turns it into a skill stat.
//
// All weights are here so the balance is easy to tune.
const POINTS_PER_METER = 10;      // height is the main driver
const COIN_COMPLETION_MAX = 1500; // points for collecting 100% of coins
const PERFECT_COIN_BONUS = 500;   // extra for a flawless 100% sweep
const POINTS_PER_KILL = 60;       // base value of a kill...
// ...scaled by an accuracy multiplier in [0.5 .. 1.5]: sloppy kills still count
// a little, perfect aim triples their value relative to spraying.
const ACCURACY_FLOOR = 0.5;

export const computeScore = (game) => {
  const height = Math.max(0, Math.floor(game.score || 0));

  const collected = game.coinsCollectedCount || 0;
  const spawned = game.coinsSpawnedTotal || 0;
  const coinPct = spawned > 0 ? collected / spawned : 0;

  const shots = game.shotsFired || 0;
  const hits = game.enemyHits || 0;
  const kills = game.enemiesKilled || 0;
  // Accuracy only means anything once you've fired; a pure dodger has no kills
  // and therefore no combat score, which is fair (no penalty for not shooting).
  const accuracy = shots > 0 ? Math.min(1, hits / shots) : 0;

  const heightScore = height * POINTS_PER_METER;
  const coinScore = Math.round(coinPct * COIN_COMPLETION_MAX);
  const perfectBonus = spawned > 0 && collected >= spawned ? PERFECT_COIN_BONUS : 0;
  const combatScore = Math.round(kills * POINTS_PER_KILL * (ACCURACY_FLOOR + accuracy));

  const total = heightScore + coinScore + combatScore + perfectBonus;

  return {
    height,
    coinsCollected: collected,
    coinsSpawned: spawned,
    coinPct,
    accuracy,
    kills,
    heightScore,
    coinScore,
    combatScore,
    perfectBonus,
    total,
  };
};
